import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../api";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, Loader2, Mic2, Share2, Store, Bell, Check, Heart } from "lucide-react";
import EventDayGroups from "../components/EventDayGroups";
import { applyMeta } from "../lib/meta";
import { toast } from "../hooks/use-toast";

export default function ActPage() {
  const { slug, actSlug } = useParams();
  const { user } = useAuth();
  const isFan = user?.role === "fan";
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [email, setEmail] = useState("");
  const [followed, setFollowed] = useState(false);
  const [following, setFollowing] = useState(false);

  useEffect(() => {
    const url = slug ? `/store/${slug}/acts/${actSlug}` : `/acts/${actSlug}`;
    api.get(url).then(({ data }) => {
      setData(data);
      applyMeta({
        title: `${data.act.name} — events & tickets · TicketsPro.app`,
        description: data.act.bio || `See all upcoming events by ${data.act.name} on TicketsPro and book tickets securely.`,
        image: data.act.image_url,
      });
      if (isFan && data.act?.slug) {
        api.get("/fan/following").then(({ data: f }) => {
          if ((f.acts || []).some((a) => a.slug === data.act.slug)) setFollowed(true);
        }).catch(() => {});
      }
    }).catch(() => setErr(true));
  }, [slug, actSlug, isFan]);

  const share = () => {
    navigator.clipboard?.writeText(window.location.href);
    toast({ title: "Link copied", description: "Share this artist page anywhere." });
  };

  const doUnfollow = async () => {
    if (!data?.act?.slug) return;
    setFollowing(true);
    try {
      await api.post(`/fan/unfollow`, { slug: data.act.slug });
      setFollowed(false);
      toast({ title: "Unfollowed " + data.act.name });
    } catch (err) {
      toast({ title: "Couldn't unfollow", variant: "destructive" });
    } finally { setFollowing(false); }
  };

  const doFollow = async (addr) => {
    if (!data?.act?.slug) return;
    setFollowing(true);
    try {
      if (isFan) {
        await api.post(`/fan/follow`, { slug: data.act.slug });
      } else {
        if (!addr) { setFollowing(false); return; }
        await api.post(`/acts/${data.act.slug}/follow`, { email: addr });
      }
      setFollowed(true);
      toast({ title: "You're following " + data.act.name, description: "We'll email you when they announce a new date." });
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === "string" ? d : Array.isArray(d) ? (d[0]?.msg || "Please try again") : "Check your email and try again";
      toast({ title: "Couldn't follow", description: msg, variant: "destructive" });
    } finally { setFollowing(false); }
  };
  const follow = (e) => { e.preventDefault(); doFollow(email.trim()); };

  return (
    <div className="min-h-screen bg-[#0d1626]" data-testid="act-page">
      <div className="border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to={data?.business ? `/store/${data.business.slug}` : "/"} className="text-white/80 hover:text-white flex items-center gap-1.5 text-sm font-medium"><ArrowLeft size={16} /> {data?.business ? data.business.name : "Home"}</Link>
          <span className="text-white font-bold">TicketsPro.app</span>
        </div>
      </div>

      {err ? (
        <div className="max-w-5xl mx-auto px-6 py-24 text-center" data-testid="act-not-found">
          <p className="text-[#8a97a8]">We couldn't find that artist.</p>
          <Link to="/events" className="inline-block mt-4 text-[#2ec9c9] hover:underline">Browse all events</Link>
        </div>
      ) : data === null ? (
        <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-[#2ec9c9]" size={32} /></div>
      ) : (
        <>
          <div className="relative overflow-hidden border-b border-white/10" style={{ background: "linear-gradient(135deg, #3a1250, #0d1626 70%)" }}>
            <div className="absolute -right-16 -top-16 w-72 h-72 rounded-full bg-[#8b5cf6]/20 blur-3xl" />
            <div className="relative max-w-5xl mx-auto px-6 py-16 flex items-center gap-5">
              <span className="w-16 h-16 rounded-2xl overflow-hidden flex items-center justify-center bg-[#8b5cf6]/20 text-[#c4b5fd] shrink-0">
                {data.act.image_url ? <img src={data.act.image_url} alt={data.act.name} className="w-full h-full object-cover" /> : <Mic2 size={28} />}
              </span>
              <div className="min-w-0">
                <p className="text-sm uppercase tracking-wide text-[#c4b5fd]">Artist</p>
                <h1 className="text-3xl sm:text-5xl font-bold text-white truncate">{data.act.name}</h1>
                {data.business && <p className="text-sm text-[#8a97a8] mt-1 flex items-center gap-1.5"><Store size={13} /> {data.business.name}</p>}
              </div>
              <button onClick={share} data-testid="act-share-btn" className="ml-auto shrink-0 inline-flex items-center gap-2 text-sm text-white/80 hover:text-white border border-white/20 rounded-full px-4 py-2">
                <Share2 size={15} /> Share
              </button>
            </div>
          </div>

          <div className="max-w-5xl mx-auto px-6 py-12">
            {data.act.bio && <p className="text-[#b7c2d3] leading-relaxed max-w-3xl mb-8">{data.act.bio}</p>}

            <div className="mb-10 rounded-2xl border border-[#8b5cf6]/30 bg-[#8b5cf6]/[0.07] p-5 max-w-xl" data-testid="act-follow">
              {followed ? (
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <p className="text-[#c4b5fd] flex items-center gap-2 font-medium"><Check size={18} /> You're following {data.act.name}{isFan ? " — see your dates in " : " — we'll email you about new dates."}{isFan && <Link to="/following" className="underline">your feed</Link>}</p>
                  {isFan && <button onClick={doUnfollow} disabled={following} data-testid="act-unfollow-btn" className="text-sm text-[#8a97a8] hover:text-white border border-white/20 rounded-full px-4 py-1.5">Unfollow</button>}
                </div>
              ) : isFan ? (
                <>
                  <p className="text-white font-semibold flex items-center gap-2"><Heart size={18} className="text-[#c4b5fd]" /> Follow {data.act.name}</p>
                  <p className="text-sm text-[#8a97a8] mt-1 mb-3">Add them to your <Link to="/following" className="underline text-[#c4b5fd]">following feed</Link> and get an email on every new date.</p>
                  <button onClick={() => doFollow((user.email || "").toLowerCase())} disabled={following} data-testid="act-follow-btn"
                    className="rounded-xl px-5 py-2.5 text-sm font-medium bg-[#8b5cf6] text-white hover:bg-[#7c3aed] disabled:opacity-60 inline-flex items-center gap-2">
                    {following ? <Loader2 className="animate-spin" size={16} /> : <Heart size={15} />} Follow
                  </button>
                </>
              ) : (
                <>
                  <p className="text-white font-semibold flex items-center gap-2"><Bell size={18} className="text-[#c4b5fd]" /> Follow {data.act.name}</p>
                  <p className="text-sm text-[#8a97a8] mt-1 mb-3">Drop your email and we'll let you know the moment they announce a new date. <Link to="/following" className="underline text-[#c4b5fd]">Have an account?</Link></p>
                  <form onSubmit={follow} className="flex gap-2">
                    <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="act-follow-email"
                      placeholder="you@email.com" className="flex-1 min-w-0 w-full rounded-xl bg-white/5 border border-white/15 text-white placeholder-[#6b7a90] px-4 py-2.5 outline-none focus:border-[#8b5cf6]" />
                    <button disabled={following} data-testid="act-follow-btn" className="rounded-xl px-5 py-2.5 text-sm font-medium bg-[#8b5cf6] text-white hover:bg-[#7c3aed] disabled:opacity-60 inline-flex items-center gap-2 whitespace-nowrap">
                      {following ? <Loader2 className="animate-spin" size={16} /> : <Bell size={15} />} Follow
                    </button>
                  </form>
                </>
              )}
            </div>

            {(data.events || []).length === 0 ? (
              <div className="py-16 text-center" data-testid="act-empty">
                <p className="text-[#8a97a8]">No upcoming events for {data.act.name} right now.</p>
              </div>
            ) : (
              <EventDayGroups events={data.events} testidPrefix="act" />
            )}
          </div>
        </>
      )}
    </div>
  );
}
